import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, register as apiRegister, refresh, getMe, exchangeCode, AuthUser } from '../api/authApi';
import { AUTH_SESSION_EXPIRED_EVENT } from '../api/tokenStore';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (username: string, email: string, password: string, displayName?: string) => Promise<AuthUser | null>;
  loginWithCode: (code: string) => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleSessionExpired = () => setUser(null);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  // On mount: attempt silent refresh to restore session from HttpOnly cookie.
  useEffect(() => {
    // First check if we already have a valid token in memory (e.g. same page)
    const current = getMe();
    if (current) {
      setUser(current);
      setIsLoading(false);
      return;
    }
    // Try refresh via cookie
    refresh()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const u = await apiLogin(username, password);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const register = useCallback(async (
    username: string,
    email: string,
    password: string,
    displayName?: string,
  ): Promise<AuthUser | null> => {
    const u = await apiRegister(username, email, password, displayName);
    setUser(u);
    return u;
  }, []);

  const loginWithCode = useCallback(async (code: string) => {
    const u = await exchangeCode(code);
    setUser(u);
  }, []);

  const hasRole = useCallback((role: string) => {
    return user?.roles.includes(role) ?? false;
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout, register, loginWithCode, hasRole }),
    [user, isLoading, login, logout, register, loginWithCode, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
